import i18n from 'i18next'
import { initReactI18next } from 'react-i18next'
import HttpBackend from 'i18next-http-backend'
import LanguageDetector from 'i18next-browser-languagedetector'

export const SUPPORTED_LOCALES = ['en', 'tr', 'de', 'ar', 'zh'] as const
export type AppLocale = (typeof SUPPORTED_LOCALES)[number]

export const RTL_LOCALES: ReadonlySet<AppLocale> = new Set(['ar'])

export const LOCALE_LABELS: Record<AppLocale, string> = {
  en: 'English',
  tr: 'Türkçe',
  de: 'Deutsch',
  ar: 'العربية',
  zh: '中文',
}

/**
 * Map app locale → BCP-47 tag used by `Intl` for numbers / currency.
 */
export const INTL_LOCALE: Record<AppLocale, string> = {
  en: 'en-US',
  tr: 'tr-TR',
  de: 'de-DE',
  ar: 'ar-SA',
  zh: 'zh-CN',
}

export function isAppLocale(value: string): value is AppLocale {
  return (SUPPORTED_LOCALES as readonly string[]).includes(value)
}

export function applyDocumentDirection(locale: AppLocale): void {
  const dir = RTL_LOCALES.has(locale) ? 'rtl' : 'ltr'
  document.documentElement.lang = locale
  document.documentElement.dir = dir
}

void i18n
  .use(HttpBackend)
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    fallbackLng: 'en',
    supportedLngs: [...SUPPORTED_LOCALES],
    ns: ['common', 'trading'],
    defaultNS: 'common',
    backend: {
      // Lazy-loaded JSON namespaces under /public/locales/{lng}/{ns}.json
      loadPath: '/locales/{{lng}}/{{ns}}.json',
    },
    detection: {
      order: ['localStorage', 'navigator'],
      caches: ['localStorage'],
      lookupLocalStorage: 'velox.locale',
    },
    interpolation: {
      escapeValue: false,
    },
    react: {
      useSuspense: true,
    },
  })

i18n.on('languageChanged', (lng) => {
  if (isAppLocale(lng)) {
    applyDocumentDirection(lng)
  }
})

export default i18n
